import { useDataSource } from 'hooks/dataSource/useDataSource'
import {
  useLazyGetMyTasksQuery,
  useUpdateTaskDurationMutation,
} from '@crew/apis/task/taskApis'
import { TaskDuration } from '@crew/apis/task/models/updateTaskDuration/request'
import dayjs from '@crew/modules/dayjs'
import { JsonDateFormat } from '@crew/enums/system'
import { cloneDeep } from 'lodash'
import { GetMyTasksRequest } from '@crew/apis/task/models/getMyTasks/request'
import { RefObject } from 'react'
import { Scheduler } from 'devextreme-react'

//  DataSourceのloadOptions.filterがany型のため、
//  パラメータがSearchMeTasksParam型かをチェックする型ガード関数
const isSearchMeTasksParam = (filter: any): filter is SearchMeTasksParam => {
  return (
    typeof filter === 'object' &&
    filter !== null &&
    ('keyword' in filter || 'taskKindId' in filter)
  )
}

export type SearchMeTasksParam = {
  /** キーワード */
  keyword?: string
  /** タスク種別ID */
  taskKindId?: string
}

export const useMyTaskListCalendar = (schedulerRef: RefObject<Scheduler>) => {
  const [updateTaskDurationMutation] = useUpdateTaskDurationMutation()
  const [lazyGetMyTasksQuery] = useLazyGetMyTasksQuery()

  // マイタスク一覧のDataSource
  const tasksDataSource = useDataSource(
    () => ({
      key: 'id',
      load: async (loadOptions) => {
        if (!schedulerRef.current) return []

        // パラメータの型が一致していない場合は空配列を返却
        if (!(loadOptions.filter && isSearchMeTasksParam(loadOptions.filter))) {
          return []
        }

        // カレンダーの表示期間（開始日、末日）を取得
        const targetDateFrom = schedulerRef.current.instance.getStartViewDate()
        const targetDateTo = schedulerRef.current.instance.getEndViewDate()

        // マイタスク一覧取得APIのパラメータを設定
        const params: GetMyTasksRequest = {
          keyword: loadOptions.filter.keyword,
          taskKindId: loadOptions.filter.taskKindId,
          targetDateFrom: dayjs(targetDateFrom).format(JsonDateFormat.YYYYMMDD),
          targetDateTo: dayjs(targetDateTo).format(JsonDateFormat.YYYYMMDD),
        }

        // マイタスク一覧を取得
        const response = await lazyGetMyTasksQuery(params).unwrap()

        // response.tasksは読み取り専用のオブジェクトのため、カレンダー上にタスクをドラグしたときresponse.tasks内の項目を直接編集することができない。
        // そのため、別のオブジェクトにresponse.tasksをクローンし編集できるようにする。
        const tasksClone = cloneDeep(response.tasks)

        // カレンダー表示用にタスクの開始日・期日を調整
        const tasks = tasksClone.map((task) => {
          const tempTask = Object.assign({}, task)

          // 開始日が未設定・期日が設定済みの場合
          // Figmaに書いてあるとおり、期日指定されている一日で表示する
          if (!task.startDate && task.dueDate) {
            return {
              ...tempTask,
              startDate: task.dueDate,
            }
          }

          // 開始日が設定済み・期日が未設定の場合
          // 表示しない
          if (task.startDate && !task.dueDate) {
            return {
              ...tempTask,
              startDate: null,
            }
          }

          return {
            ...tempTask,
          }
        })

        return tasks
      },
      update: async (key, values) => {
        const payload: TaskDuration = {
          taskId: key,
          startDate: values.startDate
            ? dayjs(values.startDate).format(JsonDateFormat.YYYYMMDD)
            : undefined,
          dueDate: values.dueDate
            ? dayjs(values.dueDate).format(JsonDateFormat.YYYYMMDD)
            : undefined,
          assignToUserId: values.assignToUser
            ? values.assignToUser.id
            : undefined,
          version: values.version,
        }

        // タスク期間更新APIを実行
        await updateTaskDurationMutation({
          task: {
            ...payload,
          },
        }).unwrap()
      },
    }),
    [lazyGetMyTasksQuery, schedulerRef, updateTaskDurationMutation]
  )

  return {
    tasksDataSource,
  }
}
